'use client';

import { motion } from 'framer-motion';
import { getSectionMotionHeading } from '@/components/dynamic-zone/section-motion-heading';
import { components } from '@/types/strapi';
import { Metric } from './Metric';

type DynamicZoneSuccessMetricsComponent =
	components['schemas']['DynamicZoneSuccessMetricsComponent'];

interface SuccessMetricsProps extends DynamicZoneSuccessMetricsComponent {
	metrics: components['schemas']['Metric'][];
	className?: string;
	isFirst?: boolean;
}

export const SuccessMetrics = ({
	heading,
	sub_heading,
	metrics,
	isFirst = false,
}: SuccessMetricsProps) => {
	const SectionHeading = getSectionMotionHeading(isFirst);
	const hasMetrics = metrics && metrics.length > 0;

	return (
		<section className="success-metrics-section relative overflow-hidden py-8">
			<noscript>
				<style>{`
					.success-metrics-section h2[style],
					.success-metrics-section p[style],
					.success-metrics-section div[style*='opacity'],
					.success-metrics-section div[style*='scale'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			{/* Enhanced background with more subtle gradients */}
			<div className="absolute inset-0 bg-gradient-to-b from-gray-50/80 to-white/90 backdrop-blur-sm" />
			<div className="bg-grid-gray-100/20 absolute inset-0 bg-[size:24px_24px]" />

			<div className="container relative mx-auto px-4">
				<div className="mx-auto mb-20 max-w-4xl text-center">
					{(heading || hasMetrics) && (
						<SectionHeading
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.5 }}
							className={`mb-6 bg-gradient-to-r from-gray-900 via-gray-700 to-gray-900 bg-clip-text text-5xl font-bold tracking-tight sm:text-6xl${!heading ? 'sr-only' : ''}`}>
							{heading || 'Key metrics'}
						</SectionHeading>
					)}
					<motion.p
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.5, delay: 0.2 }}
						className="text-xl leading-relaxed text-gray-600">
						{sub_heading}
					</motion.p>
				</div>

				<div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
					{metrics
						?.slice()
						.reverse()
						.map((metric, index) => (
							<Metric key={index} {...metric} />
						))}
				</div>
			</div>
		</section>
	);
};
